import React from "react";
import { Button, Chip, Stack, Typography } from "@mui/material";
import { blueGrey, grey } from "@mui/material/colors";

import { LottieIcon, PageContainer } from "src/shared/ui";
import { LottieIconNames } from "src/shared/config";
import mathrixaiImage from "src/shared/assets/mathrixai_image.png";

export const AboutPage: React.FC = () => {
  return (
    <PageContainer
      hidden
      mainComponent={
        <>
          <Stack
            width={1}
            height={1}
            direction="column"
            justifyContent="center"
            alignItems="flex-start"
            spacing={2}
            zIndex={99}
            sx={{
              alignItems: {
                xs: "center",
                sm: "flex-start",
              },
            }}
          >
            <Typography
              variant="h1"
              fontWeight="bold"
              color={blueGrey[700]}
              textTransform="uppercase"
              textAlign="center"
            >
              About Us
            </Typography>

            <Typography
              variant="h5"
              color={grey[700]}
              textAlign="start"
              textTransform="uppercase"
              whiteSpace="pre-wrap"
              sx={{
                textAlign: {
                  xs: "center",
                  sm: "start",
                },
              }}
            >
              Your success {"\n"}is our success
            </Typography>

            <Button variant="contained" size="large">
              Contact us
            </Button>
          </Stack>

          <Stack
            sx={{
              display: {
                xs: "none",
                sm: "flex",
              },
            }}
          >
            <LottieIcon
              icon={LottieIconNames.ABOUT_US}
              autoPlay
              loop
              style={{
                position: "absolute",
                height: "calc(100% - 180px)",
                transform: "translateX(150px)",
                top: 0,
                right: 0,
              }}
            />
          </Stack>
        </>
      }
    >
      <Stack width={1} maxWidth="lg" direction="column" gap={2}>
        <Stack width={1} direction="row" gap={2}>
          <Stack flex={1} direction="column" gap={2}>
            <Typography
              variant="h4"
              color={blueGrey[600]}
              letterSpacing={4}
              textTransform="uppercase"
            >
              Why Choose MathrixAi?
            </Typography>
            <Typography variant="body1" fontWeight={300}>
              MathrixAI is a forward-thinking technology company dedicated to
              transforming industries through innovation. With a strong
              foundation in research, problem-solving, and creative engineering,
              we develop solutions that simplify complexity and enhance
              decision-making for businesses worldwide.
            </Typography>
            <Typography
              variant="h4"
              color={blueGrey[600]}
              letterSpacing={4}
              textTransform="uppercase"
            >
              Our Vision
            </Typography>
            <Typography variant="body1" fontWeight={300}>
              We envision a world where technology empowers organizations to
              operate more efficiently, adapt to change, and unlock new
              opportunities. By blending expertise with practical applications,
              we strive to create intelligent systems that bring meaningful
              impact across various sectors.
            </Typography>

            <Typography
              variant="h4"
              color={blueGrey[600]}
              letterSpacing={4}
              textTransform="uppercase"
            >
              Our Approach
            </Typography>
            <Typography variant="body1" fontWeight={300}>
              At MathrixAI, our team thrives on a mindset of curiosity,
              collaboration, and continuous improvement. Our approach is defined
              by:
            </Typography>

            <Stack direction="column" gap={1}>
              <Stack
                flex={3}
                width={1}
                direction="row"
                alignItems="center"
                gap={2}
              >
                <Chip
                  label="Innovation-First Mentality"
                  color="warning"
                  sx={{
                    width: 190,
                    minWidth: 190,
                  }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={300}
                  color="primary.dark"
                >
                  We push boundaries, constantly exploring new possibilities to
                  stay ahead of industry trends.
                </Typography>
              </Stack>
              <Stack width={1} direction="row" alignItems="center" gap={2}>
                <Chip
                  label="Solution-Oriented Thinking"
                  color="warning"
                  sx={{
                    width: 190,
                    minWidth: 190,
                  }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={300}
                  color="primary.dark"
                >
                  Every challenge presents an opportunity; we focus on
                  delivering clear, measurable value.
                </Typography>
              </Stack>
              <Stack width={1} direction="row" alignItems="center" gap={2}>
                <Chip
                  label="Commitment to Excellence"
                  color="warning"
                  sx={{
                    width: 190,
                    minWidth: 190,
                  }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={300}
                  color="primary.dark"
                >
                  Precision and reliability drive our work, ensuring
                  high-quality outcomes.
                </Typography>
              </Stack>
              <Stack width={1} direction="row" alignItems="center" gap={2}>
                <Chip
                  label="Collaborative Culture"
                  color="warning"
                  sx={{
                    width: 190,
                    minWidth: 190,
                  }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={300}
                  color="primary.dark"
                >
                  We believe the best ideas emerge through teamwork,
                  knowledge-sharing, and a shared passion for problem-solving.
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Stack
            flex={1}
            sx={{
              display: {
                xs: "none",
                md: "flex",
              },
            }}
          >
            <img src={mathrixaiImage} alt="mathrixaiImage" />
          </Stack>
        </Stack>

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Our Team
        </Typography>
        <Typography variant="body1" fontWeight={300}>
          At MathrixAI, we believe that innovation is a collective effort. Led
          by our CEO, Sven Badalyan, our team is a dynamic group of researchers,
          engineers, and strategists who bring together diverse expertise and
          perspectives to tackle complex challenges. We foster a culture of
          collaboration, curiosity, and continuous growth, where every team
          member is encouraged to push boundaries and explore new ideas. Our
          work thrives on open communication, shared knowledge, and an iterative
          approach to problem-solving, ensuring that we not only develop
          cutting-edge solutions but also refine and improve them based on
          real-world impact. What sets our team apart is a deep commitment to
          excellence—we don’t just build technology; we craft solutions that are
          reliable, adaptable, and valuable. We embrace challenges as
          opportunities to learn and innovate, combining analytical thinking
          with creativity to deliver meaningful results. With a shared passion
          for making a difference, our team works closely with partners and
          clients worldwide, ensuring that our solutions are not only
          technologically advanced but also practical, scalable, and tailored to
          real business needs.
        </Typography>

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Global Reach
        </Typography>
        <Typography variant="body1" fontWeight={300}>
          With headquarters in Berlin, Germany, and a research hub in Yerevan,
          Armenia, MathrixAI collaborates with partners and clients worldwide,
          adapting our expertise to meet the needs of different industries and
          markets.
        </Typography>
      </Stack>
    </PageContainer>
  );
};
